import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Upload, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { modesService } from "@/api/services/modes.service";
import { toast } from "react-toastify";
import type { Mode, CreateModeRequest } from "@/types/api.types";

interface ModeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Partial<Mode> | null;
  isEditing: boolean;
  onSave: (data: CreateModeRequest) => void;
  isSaving: boolean;
}

export default function ModeForm({
  open,
  onOpenChange,
  mode,
  isEditing,
  onSave,
  isSaving,
}: ModeFormProps) {
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateModeRequest>({
    defaultValues: {
      name: "",
      description: "",
      logo: "",
      route: "",
      slug: "",
      isActive: true,
      order: 0,
    },
  });

  useEffect(() => {
    if (mode) {
      reset({
        name: mode.name || "",
        description: mode.description || "",
        logo: mode.logo || "",
        route: mode.route || "",
        slug: mode.slug || "",
        isActive: mode.isActive ?? true,
        order: mode.order || 0,
      });
      setLogoPreview(mode.logo || "");
    }
  }, [mode, reset]);

  // Auto-generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (!isEditing) {
      setValue("slug", generateSlug(name));
    }
  };

  // Handle image upload
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setIsUploading(true);

    try {
      // Upload to backend
      const response = await modesService.uploadLogo(file);

      // Set the URL in form
      setValue("logo", response.url);
      setLogoPreview(response.url);

      toast.success("Logo uploaded successfully");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload logo");
    } finally {
      setIsUploading(false);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setValue("logo", "");
    setLogoPreview("");
  };

  const onSubmit = (data: CreateModeRequest) => {
    onSave(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            {isEditing ? "Edit Mode" : "Add New Mode"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              {...register("name", { required: "Name is required" })}
              onChange={handleNameChange}
              placeholder="FIFA World Cup 2026"
              className="bg-white border-gray-300"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Slug */}
          <div>
            <Label htmlFor="slug" className="text-gray-700">
              Slug <span className="text-red-500">*</span>
            </Label>
            <Input
              id="slug"
              {...register("slug", { required: "Slug is required" })}
              placeholder="fifa-worldcup-2026"
              className="bg-white border-gray-300"
            />
            <p className="text-xs text-gray-500 mt-1">
              URL-friendly name (auto-generated from name)
            </p>
            {errors.slug && (
              <p className="text-sm text-red-500 mt-1">{errors.slug.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description" className="text-gray-700">
              Description
            </Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder="Everything about the upcoming FIFA World Cup 2026"
              rows={3}
              className="bg-white border-gray-300"
            />
          </div>

          {/* Route */}
          <div>
            <Label htmlFor="route" className="text-gray-700">
              Route <span className="text-red-500">*</span>
            </Label>
            <Input
              id="route"
              {...register("route", { required: "Route is required" })}
              placeholder="/(root)/modes/fifa-worldcup"
              className="bg-white border-gray-300"
            />
            <p className="text-xs text-gray-500 mt-1">
              Frontend navigation path
            </p>
            {errors.route && (
              <p className="text-sm text-red-500 mt-1">
                {errors.route.message}
              </p>
            )}
          </div>

          {/* Logo Upload */}
          <div>
            <Label htmlFor="logo" className="text-gray-700">
              Logo
            </Label>

            {/* Preview */}
            {logoPreview && (
              <div className="mt-2 relative inline-block">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-32 h-32 object-contain border rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Upload Button */}
            <div className="mt-2">
              <label
                htmlFor="logo-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-md cursor-pointer hover:bg-blue-100 transition"
              >
                <Upload className="h-4 w-4" />
                {isUploading ? "Uploading..." : "Upload Logo"}
              </label>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={isUploading}
                className="hidden"
              />
            </div>

            {/* Or URL Input */}
            <div className="mt-3">
              <Input
                {...register("logo")}
                placeholder="Or paste logo URL"
                type="url"
                className="bg-white border-gray-300"
                onChange={(e) => setLogoPreview(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                You can either upload an image or paste a URL
              </p>
            </div>
          </div>

          {/* Order */}
          <div>
            <Label htmlFor="order" className="text-gray-700">
              Display Order
            </Label>
            <Input
              id="order"
              {...register("order", { valueAsNumber: true })}
              type="number"
              placeholder="0"
              className="bg-white border-gray-300"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lower numbers appear first
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-3">
            <Switch
              id="isActive"
              checked={watch("isActive")}
              onCheckedChange={(checked) => setValue("isActive", checked)}
              className={`
                data-[state=checked]:bg-green-600
                data-[state=unchecked]:bg-red-500
              `}
            />
            <Label htmlFor="isActive" className="text-gray-700">
              {watch("isActive") ? "Active" : "Inactive"}
            </Label>
          </div>

          <DialogFooter className="bg-white pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving || isUploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isUploading}>
              {isSaving ? "Saving..." : isEditing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
